/**
 * Shared helpers for turning raw data ranges into clean, readable chart axes.
 */

const NICE_STEPS = [1, 2, 2.5, 5, 10];

function niceify(norm: number): number {
  return NICE_STEPS.find((step) => norm <= step) ?? 10;
}

/** Round a positive value up to a clean tick maximum (1 / 2 / 2.5 / 5 / 10 × 10ⁿ). */
export function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return niceify(value / magnitude) * magnitude;
}

/** A clean tick interval that splits a span into roughly `count` steps. */
export function niceStep(span: number, count: number): number {
  if (!Number.isFinite(span) || span <= 0 || count <= 0) return 1;
  const raw = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return niceify(raw / magnitude) * magnitude;
}

/** Snap a value range to clean, evenly spaced tick boundaries. */
export function niceBounds(
  min: number,
  max: number,
  count = 4,
): { top: number; bottom: number; step: number } {
  let lo = min;
  let hi = max;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { top: 1, bottom: 0, step: 0.25 };
  }
  if (hi - lo <= Math.abs(hi) * 0.02) {
    const pad = Math.max(Math.abs(hi) * 0.05, 1);
    lo -= pad;
    hi += pad;
  }
  const step = niceStep(hi - lo, count);
  const bottom = Math.floor(lo / step) * step;
  const top = Math.ceil(hi / step) * step;
  return { top, bottom, step };
}

/** Build tick values from `bottom` to `top` (inclusive), largest first. */
export function buildTicks(bottom: number, top: number, step: number): number[] {
  if (!Number.isFinite(step) || step <= 0 || top <= bottom) return [top, bottom];
  const rounds = Math.round((top - bottom) / step);
  const ticks: number[] = [];
  for (let i = 0; i <= rounds; i += 1) {
    ticks.push(Math.round((bottom + i * step) * 1e6) / 1e6);
  }
  return ticks.reverse();
}

/** Compact axis label, e.g. 7200 → "7,200", 12400 → "12.4k". */
export function formatAxisValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 10000) {
    const scaled = value / 1000;
    return `${scaled.toFixed(scaled % 1 === 0 ? 0 : 1)}k`;
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Percentage axis label, e.g. "0%", "-6.5%", or "+42%" when signed. */
export function formatPercentTick(value: number, decimals = 0, signed = false): string {
  if (value === 0) return '0%';
  const text = `${Math.abs(value).toFixed(decimals)}%`;
  if (value < 0) return `-${text}`;
  return signed ? `+${text}` : text;
}

/** How many months to skip between labels before the axis gets too busy. */
export function barLabelStride(count: number, maxLabels = 12): number {
  return count > maxLabels ? Math.ceil(count / maxLabels) : 1;
}

/**
 * Whether the month at `index` should carry an x-axis label. The first and last
 * months are always labelled so the axis stays readable end to end. Shared by
 * both the bar labels and their guide dots so the two never disagree.
 */
export function isBarLabeled(index: number, count: number, maxLabels = 12): boolean {
  if (count <= 0 || index < 0 || index >= count) return false;
  const stride = barLabelStride(count, maxLabels);
  return stride === 1 || index === 0 || index === count - 1 || index % stride === 0;
}

/**
 * Mobile-specific label selection with minimum gap enforcement.
 *
 * Algorithm:
 *  1. Always include first (0) and last (n-1) month.
 *  2. Fill in between at fixed `step` intervals.
 *  3. If the last interval label is too close to the final month (< step),
 *     remove it so the two labels don't collide.
 */
export function selectMobileBarLabels(count: number, step: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const result: number[] = [0];
  for (let i = step; i < count - 1; i += step) {
    result.push(i);
  }
  // Always include last
  const last = count - 1;
  // If the last included label is too close to the final month, remove it
  if (result.length >= 2 && last - result[result.length - 1] < step) {
    result.pop();
  }
  result.push(last);
  return result;
}

export interface YearDivider {
  /** Horizontal position of the divider, as a percentage of the plot width. */
  x: number;
  /** Year shown in the small badge at the top of the divider. */
  label: string;
}

/**
 * Find every point where the calendar year rolls over and return a divider for
 * it. Divider positions that drift with the point spacing (curves) come from
 * `xPct`; the bar charts pass a midpoint instead so the line lands in the gap
 * between the last bar of one year and the first of the next.
 */
export function buildYearDividers<T extends { ts?: number }>(
  points: T[],
  xPct: (index: number) => number,
): YearDivider[] {
  const dividers: YearDivider[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const ts = points[i].ts;
    const prevTs = points[i - 1].ts;
    if (typeof ts !== 'number' || typeof prevTs !== 'number') continue;
    const year = new Date(ts).getUTCFullYear();
    const prevYear = new Date(prevTs).getUTCFullYear();
    if (year !== prevYear) dividers.push({ x: xPct(i), label: String(year) });
  }
  return dividers;
}